// Layer 4 (UI) — the Checks & inspections tab (polish.P20, comp A
// `checksBody`): one card, the shift and its progress in the title row, six
// checks divided by hairlines. A done check is an ink-filled box; an at-risk
// check carries a 3px state-warning rule and its reason in warning ink, with
// an open case's code linking into Advisory (adjudication 7). Renders
// `shiftChecklist(persona)` only.

import { Link } from 'react-router-dom';
import { cn } from '@/shared/cn';
import type { Persona } from '@/shared/types';
import { DowntimeCard } from '../downtime/DowntimeCard';
import { ToneChip } from '../process/ProcessUi';
import { shiftChecklist, type ShiftCheckReason } from './ShiftChecklist.transform';
import { FailureModeTag, OwnerChip, REF_LINK } from './risk-ui';

function Reason({ reason, projectId }: { reason: ShiftCheckReason; projectId: string }) {
  return (
    <span className="flex flex-wrap items-center gap-1.5 text-xs font-semibold text-[var(--state-warning)]" data-check-reason={reason.kind}>
      <i aria-hidden className="inline-block size-1.5 rounded-full bg-[var(--state-warning)]" />
      {reason.kind === 'loop' ? (
        reason.text
      ) : (
        <>
          Case
          <Link to={`/projects/${projectId}/advisory/case/${reason.caseId}`} className={REF_LINK} data-case-link={reason.caseId}>
            {reason.code}
          </Link>
          {reason.state}
        </>
      )}
    </span>
  );
}

export function ShiftChecklist({ persona, projectId }: { persona: Persona; projectId: string }) {
  const view = shiftChecklist(persona);
  const pct = view.total === 0 ? 0 : (view.done / view.total) * 100;
  return (
    <DowntimeCard
      title="Shift checks"
      actions={
        <span className="flex items-center gap-2 text-xs tabular-nums text-muted" data-checks-header={view.header}>
          <span>
            {view.shiftLabel} · <span className="font-semibold text-ink-primary">{view.done}/{view.total} complete</span>
          </span>
          <span className="relative block h-1.5 w-[120px] overflow-hidden rounded-full bg-[var(--hairline)]" aria-hidden>
            <i className="absolute inset-y-0 left-0 block rounded-full bg-[var(--ink-secondary)]" style={{ width: `${pct}%` }} />
          </span>
        </span>
      }
      className="px-0 pb-0 [&>header]:mb-0 [&>header]:border-b [&>header]:border-[var(--hairline)] [&>header]:px-4 [&>header]:pb-2.5"
    >
      <ul className="divide-y divide-[var(--color-divider)]">
        {view.rows.map((row) => (
          <li
            key={row.id}
            data-check={row.id}
            data-at-risk={row.atRisk ? 'true' : 'false'}
            className={cn(
              'grid grid-cols-[20px_minmax(0,1fr)_auto] items-start gap-x-3 gap-y-1 px-4 py-2.5',
              row.atRisk && 'shadow-[inset_3px_0_0_var(--state-warning)]',
            )}
          >
            <span
              aria-label={row.done ? 'done' : 'open'}
              className={cn(
                'mt-0.5 grid size-4 place-items-center rounded-[5px] border-[1.5px]',
                row.done ? 'border-ink-primary bg-ink-primary text-[var(--surface-2)]' : 'border-[var(--hairline-strong)]',
              )}
            >
              {row.done ? (
                <svg viewBox="0 0 10 10" className="size-2.5" aria-hidden>
                  <path d="M2 5.2 4.1 7.3 8 2.9" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              ) : null}
            </span>
            <span className={cn('text-[13px] font-semibold', row.done ? 'text-ink-secondary' : 'text-ink-primary')}>{row.title}</span>
            <span className="row-span-4 justify-self-end">
              <OwnerChip>{row.ownerPersona}</OwnerChip>
            </span>
            <span className="col-start-2 text-xs leading-snug text-ink-secondary">{row.description}</span>
            {row.reasons.length > 0 ? (
              <span className="col-start-2 flex flex-wrap gap-x-3 gap-y-1">
                {row.reasons.map((reason, index) => (
                  <Reason key={index} reason={reason} projectId={projectId} />
                ))}
              </span>
            ) : null}
            <span className="col-start-2 mt-0.5 flex flex-wrap items-center gap-1.5">
              <ToneChip tone="neutral">{row.frequency}</ToneChip>
              <ToneChip tone={row.riskLevel === 'high' ? 'warn' : 'neutral'}>{row.riskLevelLabel}</ToneChip>
              {row.failureModeLabels.map((label) => (
                <FailureModeTag key={label}>{label}</FailureModeTag>
              ))}
            </span>
          </li>
        ))}
      </ul>
    </DowntimeCard>
  );
}
